import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { environment } from '../../environments/environment';

export interface PlatformSupportsSavedCardsResponseDto {
  supportsSavedCards: boolean;
  paymentProvider?: string | null;
}

export interface ApiResult<T> {
  success: boolean;
  message: string;
  data?: T;
}

/**
 * Caches supports-saved-cards result per platformId + environment to avoid repeated API calls.
 */
const cache = new Map<string, { supportsSavedCards: boolean; paymentProvider?: string | null }>();

function cacheKey(platformId: string, env: string): string {
  return `${platformId}_${env}`;
}

@Injectable({
  providedIn: 'root'
})
export class ArkifiHubPlatformService {
  private apiUrl = `${environment.apiUrl}/api/ArkifiHubPlatform`;

  constructor(private http: HttpClient) {}

  /**
   * Check if the store (platform) supports saved cards (Paystack or Monnify only).
   * Result is cached per platformId + environment.
   * When false, UI should hide Add Card button and disallow paying with saved cards (checkout and bills).
   */
  getSupportsSavedCards(
    platformId: string,
    environment: 'Live' | 'Test'
  ): Observable<ApiResult<PlatformSupportsSavedCardsResponseDto>> {
    const key = cacheKey(platformId, environment);
    const cached = cache.get(key);
    if (cached !== undefined) {
      return of({
        success: true,
        message: 'OK',
        data: {
          supportsSavedCards: cached.supportsSavedCards,
          paymentProvider: cached.paymentProvider
        }
      });
    }

    const url = `${this.apiUrl}/platform/${platformId}/supports-saved-cards?environment=${environment}`;
    return this.http.get<ApiResult<PlatformSupportsSavedCardsResponseDto>>(url).pipe(
      map((res) => {
        if (res.success && res.data) {
          cache.set(key, {
            supportsSavedCards: res.data.supportsSavedCards,
            paymentProvider: res.data.paymentProvider ?? null
          });
        }
        return res;
      }),
      catchError(() =>
        of({
          success: false,
          message: 'Failed to check saved cards support',
          data: { supportsSavedCards: false, paymentProvider: null }
        })
      )
    );
  }

  /**
   * Clear cached result for a platform (e.g. when switching store).
   * Optional: call when platformId or environment changes.
   */
  clearCache(platformId?: string, environment?: string): void {
    if (!platformId && !environment) {
      cache.clear();
      return;
    }
    if (platformId && environment) {
      cache.delete(cacheKey(platformId, environment));
    } else {
      const keysToDelete: string[] = [];
      cache.forEach((_, key) => {
        if ((platformId && key.startsWith(platformId + '_')) || (environment && key.endsWith('_' + environment))) {
          keysToDelete.push(key);
        }
      });
      keysToDelete.forEach((k) => cache.delete(k));
    }
  }
}
